import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router';

/**
 * Reusable empty state component with an inline SVG illustration,
 * a primary message, and an optional contextual action hint.
 */

/** Icon variants available for EmptyState. */
export type EmptyStateVariant = 'document' | 'success' | 'calendar' | 'archive';

/** Optional call-to-action rendered below the hint. Either a router link or a button. */
export interface EmptyStateAction {
    /** Label text for the action element. */
    label: string;
    /** Internal route path. Renders a router `<Link>` when provided. Mutually exclusive with `onClick`. */
    href?: string;
    /** Click handler. Renders a `<button>` when provided. Mutually exclusive with `href`. */
    onClick?: () => void;
}

/** Props for EmptyState. */
export interface EmptyStateProps {
    /** Primary message to display. */
    message: string;
    /** Optional contextual next-step hint shown below the message. */
    hint?: string;
    /**
     * Illustration style. 'document' for empty tables (default), 'success' for
     * all-caught-up states, 'calendar' for empty compliance-calendar states
     * (#3251), 'archive' for empty closed/archived-list states (#3298).
     */
    variant?: EmptyStateVariant;
    /** Optional call-to-action link or button rendered below the hint. */
    action?: EmptyStateAction;
}

/** Inline SVG document icon for empty table states. */
function DocumentIcon() {
    return (
        <svg
            data-testid="empty-state-icon-document"
            aria-hidden="true"
            role="img"
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect x="10" y="6" width="36" height="46" rx="3" fill="#e5e7eb" stroke="#d1d5db" strokeWidth="2" />
            <rect x="16" y="16" width="24" height="3" rx="1.5" fill="#9ca3af" />
            <rect x="16" y="24" width="20" height="3" rx="1.5" fill="#9ca3af" />
            <rect x="16" y="32" width="16" height="3" rx="1.5" fill="#9ca3af" />
            <circle cx="46" cy="46" r="12" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="2" />
            <path d="M41 46h10M46 41v10" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

/** Inline SVG checkmark circle icon for success/all-caught-up states. */
function SuccessIcon() {
    return (
        <svg aria-hidden="true" role="img" width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="32" cy="32" r="28" fill="#d1fae5" stroke="#6ee7b7" strokeWidth="2" />
            <path d="M20 32l8 8 16-16" stroke="#059669" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

/** Inline SVG calendar icon for empty compliance-calendar states (#3251). */
function CalendarIcon() {
    return (
        <svg
            data-testid="empty-state-icon-calendar"
            aria-hidden="true"
            role="img"
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            {/* Calendar body */}
            <rect x="8" y="12" width="48" height="44" rx="3" fill="#e5e7eb" stroke="#d1d5db" strokeWidth="2" />
            {/* Header bar */}
            <rect x="8" y="12" width="48" height="12" rx="3" fill="#d1d5db" />
            {/* Two hanging tabs */}
            <rect x="18" y="6" width="4" height="10" rx="2" fill="#9ca3af" />
            <rect x="42" y="6" width="4" height="10" rx="2" fill="#9ca3af" />
            {/* Grid of muted day dots */}
            <circle cx="18" cy="34" r="2.5" fill="#9ca3af" />
            <circle cx="28" cy="34" r="2.5" fill="#9ca3af" />
            <circle cx="38" cy="34" r="2.5" fill="#9ca3af" />
            <circle cx="48" cy="34" r="2.5" fill="#9ca3af" />
            <circle cx="18" cy="44" r="2.5" fill="#9ca3af" />
            <circle cx="28" cy="44" r="2.5" fill="#9ca3af" />
            <circle cx="38" cy="44" r="2.5" fill="#9ca3af" />
            <circle cx="48" cy="44" r="2.5" fill="#9ca3af" />
        </svg>
    );
}

/** Inline SVG archive (lidded box) icon for empty closed/archived-list states (#3298). */
function ArchiveIcon() {
    return (
        <svg
            data-testid="empty-state-icon-archive"
            aria-hidden="true"
            role="img"
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            {/* Lid */}
            <rect x="8" y="10" width="48" height="12" rx="3" fill="#d1d5db" stroke="#9ca3af" strokeWidth="2" />
            {/* Box body */}
            <rect x="12" y="22" width="40" height="32" rx="3" fill="#e5e7eb" stroke="#d1d5db" strokeWidth="2" />
            {/* Handle slot */}
            <rect x="26" y="32" width="12" height="4" rx="2" fill="#9ca3af" />
        </svg>
    );
}

/** Selects the illustration for the given variant. */
function VariantIcon({ variant }: { variant: EmptyStateVariant }) {
    if (variant === 'success') return <SuccessIcon />;
    if (variant === 'calendar') return <CalendarIcon />;
    if (variant === 'archive') return <ArchiveIcon />;
    return <DocumentIcon />;
}

/**
 * EmptyState displays a centered illustration, a primary message, and an optional
 * action hint. Use it wherever a list or table has no items to show.
 */
function EmptyState({ message, hint, variant = 'document', action }: EmptyStateProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 4,
                py: 6,
                textAlign: 'center',
            }}
        >
            <Box sx={{ mb: 2 }}>
                <VariantIcon variant={variant} />
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', mb: hint || action ? 1 : 0 }}>
                {message}
            </Typography>
            {hint && (
                <Typography data-testid="empty-state-hint" variant="body2" sx={{ color: 'text.secondary', mb: action ? 2 : 0 }}>
                    {hint}
                </Typography>
            )}
            {action && (
                <Box data-testid="empty-state-action" sx={{ mt: hint ? 0 : 2 }}>
                    {action.href ? (
                        <Button component={Link} to={action.href} variant="contained">
                            {action.label}
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={action.onClick}>
                            {action.label}
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default EmptyState;
